import { motion } from 'framer-motion';
import { Building2, TrendingUp, Users, Star, ShoppingBag, Dumbbell, ShoppingCart, Building, CheckCircle } from 'lucide-react';
import ContactForm from '../components/ContactForm';

const businessTypes = [
  {
    icon: ShoppingBag,
    name: 'Shoppings',
    description: 'Transforme a experiência de compra dos seus clientes.',
  },
  {
    icon: Dumbbell,
    name: 'Academias',
    description: 'Praticidade para alunos receberem entregas durante o treino.',
  },
  {
    icon: ShoppingCart,
    name: 'Supermercados',
    description: 'Ponto de retirada conveniente para compras online.',
  },
  {
    icon: Building,
    name: 'Centros Comerciais',
    description: 'Diferencial competitivo para lojistas e visitantes.',
  },
];

const benefits = [
  {
    icon: TrendingUp,
    title: 'Aumento do Fluxo',
    description: 'Clientes visitam seu estabelecimento para retirar encomendas e acabam consumindo mais.',
    stat: '+35%',
    statLabel: 'em visitas',
  },
  {
    icon: Star,
    title: 'Diferencial Competitivo',
    description: 'Destaque-se da concorrência oferecendo um serviço moderno e inovador.',
    stat: '#1',
    statLabel: 'em inovação',
  },
  {
    icon: Users,
    title: 'Fidelização',
    description: 'Clientes que usam o locker têm maior tendência a retornar ao estabelecimento.',
    stat: '89%',
    statLabel: 'retorno',
  },
];

const usageModels = [
  {
    title: 'Assinatura Mensal',
    description: 'Clientes pagam uma mensalidade fixa para uso ilimitado dos lockers.',
    price: 'R$ 59,90/mês',
    features: ['Uso ilimitado', 'Prioridade', 'Suporte 24/7'],
  },
  {
    title: 'Pagamento por Uso',
    description: 'Clientes pagam apenas quando utilizam o serviço.',
    price: 'R$ 8,90/uso',
    features: ['Sem mensalidade', 'Flexibilidade', 'Cadastro grátis'],
  },
];

export const EmpresasPage = () => {
  return (
    <main className="pt-20" data-testid="empresas-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23D4FF00' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-lokker-lime/20 px-4 py-2 rounded-sm mb-6">
                <Building2 className="w-5 h-5 text-lokker-lime" />
                <span className="text-lokker-lime font-medium">Para Empresas</span>
              </div>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-tight">
                Atraia mais clientes com lockers inteligentes
              </h1>
              <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                Ofereça um diferencial competitivo e transforme seu estabelecimento 
                em um ponto de conveniência para milhares de consumidores.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="#quero-no-meu-negocio"
                  className="btn-secondary px-8 py-4 rounded-sm inline-flex items-center gap-2"
                  data-testid="empresa-hero-cta"
                >
                  Quero no Meu Negócio
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="absolute -inset-4 border-2 border-lokker-lime rounded-sm transform -rotate-2" />
              <img
                src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=600&h=400&fit=crop"
                alt="Ambiente corporativo moderno"
                className="relative rounded-sm w-full h-auto"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Business Types */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="inline-block bg-lokker-lime/20 text-slate-700 text-sm font-medium px-4 py-2 rounded-sm mb-4">
              Segmentos
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 mb-4">
              Ideal para diversos tipos de negócio
            </h2>
            <p className="text-slate-600">
              O Use Lokker se adapta às necessidades do seu estabelecimento.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {businessTypes.map((business, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`business-type-${index + 1}`}
                className="bg-slate-50 border border-slate-200 p-6 rounded-sm text-center card-hover"
              >
                <div className="w-16 h-16 bg-lokker-blue rounded-sm flex items-center justify-center mx-auto mb-4">
                  <business.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                  {business.name}
                </h3>
                <p className="text-slate-600 text-sm">
                  {business.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits with Stats */}
      <section className="py-16 lg:py-24 bg-lokker-blue relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23FFFFFF' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="inline-block bg-lokker-lime text-black text-sm font-bold px-4 py-2 rounded-sm mb-4 uppercase tracking-wide">
              Benefícios
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
              Resultados comprovados
            </h2>
            <p className="text-white/80">
              Empresas parceiras já estão colhendo os benefícios do Use Lokker.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`empresa-benefit-${index + 1}`}
                className="bg-white p-8 rounded-sm text-center"
              >
                <div className="w-14 h-14 bg-lokker-lime rounded-sm flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-black" />
                </div>
                <div className="mb-4">
                  <span className="font-heading font-bold text-5xl text-lokker-blue">
                    {benefit.stat}
                  </span>
                  <span className="block text-sm text-slate-500 mt-1">
                    {benefit.statLabel}
                  </span>
                </div>
                <h3 className="font-heading font-semibold text-xl text-slate-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Usage Models */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="inline-block bg-lokker-lime/20 text-slate-700 text-sm font-medium px-4 py-2 rounded-sm mb-4">
              Modelos de Uso
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 mb-4">
              Flexibilidade para seus clientes
            </h2>
            <p className="text-slate-600">
              Seus clientes escolhem como querem usar o serviço.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {usageModels.map((model, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`usage-model-${index + 1}`}
                className="bg-white border border-slate-200 p-8 rounded-sm"
              >
                <h3 className="font-heading font-bold text-2xl text-slate-900 mb-2">
                  {model.title}
                </h3>
                <p className="text-slate-600 mb-4">
                  {model.description}
                </p>
                <div className="bg-slate-50 p-4 rounded-sm mb-6">
                  <span className="font-heading font-bold text-3xl text-lokker-blue">
                    {model.price}
                  </span>
                </div>
                <ul className="space-y-2">
                  {model.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-2 text-slate-700">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works for Business */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 mb-4">
              Como funciona a parceria
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Contato', desc: 'Entre em contato conosco' },
              { step: '2', title: 'Análise', desc: 'Avaliamos o melhor modelo' },
              { step: '3', title: 'Instalação', desc: 'Instalamos os lockers' },
              { step: '4', title: 'Operação', desc: 'Comece a atrair clientes' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-lokker-blue rounded-sm flex items-center justify-center mx-auto mb-4">
                  <span className="font-heading font-bold text-2xl text-white">
                    {item.step}
                  </span>
                </div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="quero-no-meu-negocio" className="py-16 lg:py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block bg-lokker-lime text-black text-sm font-bold px-4 py-2 rounded-sm mb-4 uppercase tracking-wide">
                Fale Conosco
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
                Quero Use Lokker no meu negócio
              </h2>
              <p className="text-slate-300 mb-8 leading-relaxed">
                Preencha o formulário e nossa equipe comercial entrará em contato 
                para apresentar a melhor solução para o seu estabelecimento.
              </p>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-lokker-lime rounded-sm flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Aumente suas vendas</h4>
                    <p className="text-slate-400 text-sm">
                      Clientes que vêm buscar encomendas tendem a consumir em seu estabelecimento.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-lokker-lime rounded-sm flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Fidelize clientes</h4>
                    <p className="text-slate-400 text-sm">
                      Ofereça uma experiência diferenciada e moderna.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-lokker-lime rounded-sm flex items-center justify-center flex-shrink-0">
                    <Star className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Destaque-se</h4>
                    <p className="text-slate-400 text-sm">
                      Seja pioneiro em oferecer esse serviço na sua região.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <ContactForm
                type="empresa"
                title="Solicitar Contato"
                description="Preencha seus dados e entraremos em contato."
              />
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default EmpresasPage;
